import {
  faArrowCircleLeft,
  faCheckCircle,
  faExclamation,
  faForward,
  faSave
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import corner6 from 'assets/img/illustrations/corner-6.png';
import Flex from 'components/common/Flex';
import PageHeader from 'components/common/PageHeader';
import FaqBasicCard from 'components/pages/knowledge-center/faq/FaqBasicCard';
import { faqs } from 'data/faqs-subscription';
import draftEntryHelper from 'helpers/draft-entry-helper';
import segmentHelper from 'helpers/segment-helper';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Col, Modal, Row, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  actionSetDraftSaveError,
  actionSetDraftSaveStatus,
  createDraftOnServer,
  refreshSavedLocation,
  updateDraftOnServer
} from 'stores/app';
import { refreshDocumentCollections, refreshDocumentList } from 'stores/docs';
import { showAlert, showConfirm } from 'stores/modal';
import { actionAddNotification } from 'stores/notification';
import {
  actionLogSigningAction,
  actionReInstateSigning,
  actionReset,
  actionStart
} from 'stores/signing';
import c from 'util/const';
import logger from 'util/logger';
import utils from 'util/utils';
import { v4 as uuidv4 } from 'uuid';
import SigningLayout from './SigningLayout';
import SigningProvider from './SigningProvider';

const Signing = ({ variant, validation, progressBar }) => {
  const dispatch = useDispatch();
  const { signing } = useSelector(state => state);
  const { userMaster, draftSaveStatus } = useSelector(state => state.app);
  const [savingDraft, setSavingDraft] = useState(false);
  const navigate = useNavigate();
  const { urlDraftId } = useParams();
  const [isCompleted, setIsCompleted] = useState(false);
  const [showUnsavedDataModal, setShowUnsavedDataModal] = useState(false);
  const [initializingDraftEntry, setInitializingDraftEntry] = useState(false);
  const [initializingNewEntry, setInitializingNewEntry] = useState(false);
  const [readyToStartEntry, setReadyToStartEntry] = useState(false);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isPractice = queryParams.get('isPractice');

  const [step, setStep] = useState(1);
  const [stepTracker, setStepTracker] = useState({ next: 0 });
  const [isDraftReady, setIsDraftReady] = useState(false);
  const initialized = useRef(false);

  const { subscriptionStatus } = useSelector(state => state.subscription);
  let activeSubscriptions = _.get(subscriptionStatus, 'Active', []);

  const saveSigningToDraft = () => {
    // Do not persist until the draft (and its entry_step) has been restored.
    // Otherwise refresh mounts with step=1 and overwrites progress as step1.
    if (!isDraftReady) {
      return;
    }
    if (!userMaster || !urlDraftId || savingDraft) {
      return;
    }
    // In-memory guard: only persist when the mounted draft matches Redux state.
    // (No storage read needed — the draft_id lives in Redux.)
    if (draftEntryHelper.getDarftIdFromKey(urlDraftId) !== signing.draft_id) {
      logger.log(
        'DraftId mismatched',
        draftEntryHelper.getDarftIdFromKey(urlDraftId),
        '-----',
        signing.draft_id
      );
      return;
    }
    // Never autosave a completed draft.
    if (signing?.completedAtDate) {
      return;
    }

    setSavingDraft(true);
    dispatch(actionSetDraftSaveStatus(c.draftStatus.Saving));

    let now = moment().format();
    let currentStep = draftEntryHelper.getDraftStepName(
      stepTracker.next && stepTracker.next > 0 ? 6 : step
    );
    // `created` is the draft start time, carried in Redux as startedAtDate.
    let draftMetadata = {
      created: signing?.startedAtDate || now,
      completed_at: signing?.completedAtDate || null,
      last_updated: now,
      entry_step: currentStep.code,
      source: 'Desktop'
    };
    logger.log('Updating Meta data', draftMetadata);

    dispatch(
      updateDraftOnServer(
        urlDraftId,
        utils.encodeObjectForBlobData({
          draft_id: draftEntryHelper.getDarftIdFromKey(urlDraftId),
          draft_data: draftMetadata,
          entry_data: signing
        })
      )
    )
      .then(() => {
        dispatch(actionSetDraftSaveError(null));
        dispatch(actionSetDraftSaveStatus(c.draftStatus.Saved));
        setSavingDraft(false);
      })
      .catch(err => {
        setSavingDraft(false);
        dispatch(actionSetDraftSaveStatus(c.draftStatus.Error));
        logger.log('Error saving draft to server', err);
        // A plain connectivity drop is already communicated by the offline
        // banner + the "Draft not saved" modal, so don't also raise the generic
        // in-page red error box. Keep raising it for real (non-network) errors.
        const isNetworkErr =
          err === 'NETWORK_OFFLINE' ||
          (typeof navigator !== 'undefined' && navigator.onLine === false);
        dispatch(actionSetDraftSaveError(isNetworkErr ? null : err));
        if (err && err.Code == 'NotFound') {
          dispatch(
            showConfirm(
              'Signing draft deleted',
              'This signing draft is no longer available on your account. It may have been deleted from another device.',
              () => {}
            )
          );
        } else {
          // Reuse the existing confirm modal as the save-error surface.
          dispatch(
            showConfirm(
              'Draft not saved',
              "We couldn't save your changes to the server. Your information is still here on this screen — please try again in a moment. Don't close or refresh this page until it saves.",
              retry => {
                if (retry) {
                  saveSigningToDraft();
                }
              }
            )
          );
        }
      });
  };

  // useEffect(() => {
  //   logger.log(signing);
  // }, [signing]);

  useEffect(() => {
    if (!urlDraftId) {
      setIsDraftReady(false);
      if (userMaster) {
        initialized.current = true;
        initialiseNewEntry();
      }
      return;
    }

    // Wait for draft restore before mounting the wizard / allowing autosave.
    setIsDraftReady(false);
    if (signing.practiceTour) {
      setIsDraftReady(true);
      return;
    }
    if (!userMaster?.GUID) {
      return;
    }
    setDraftToEdit(draftEntryHelper.getDarftIdFromKey(urlDraftId));
  }, [urlDraftId, userMaster?.GUID]);

  // Guard against losing unsynced work: warn on refresh/close/navigation while a
  // save is in flight or the last save failed. (Server is the only store now.)
  useEffect(() => {
    const unsynced =
      savingDraft ||
      draftSaveStatus === c.draftStatus.Saving ||
      draftSaveStatus === c.draftStatus.Error;
    if (!urlDraftId || !unsynced) {
      return undefined;
    }
    const handler = e => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [savingDraft, draftSaveStatus, urlDraftId]);

  const createNewDraftIdOrWarn = () => {
    return new Promise((resolve, reject) => {
      draftEntryHelper
        .getNewDraftId()
        .then(newDraftId => {
          dispatch(createDraftOnServer({})).then(res => {
            logger.log('createNewDraftIdOrWarn Signing', res);
            dispatch(actionSetDraftSaveStatus(c.draftStatus.None));
            resolve(c.storage.draftEntryIdPrefix + res.GUID);
          });
        })
        .catch(err => {
          //logger.log('createNewDraftIdOrWarn catch error', err);
          dispatch(
            showAlert(
              'Drafts not available',
              'We were unable to create a new draft on your device.  Your signing will not be saved until uploaded to our server.'
            )
          );
          resolve(null);
        });
    });
  };

  const setDraftToEdit = async draft_id => {
    draftEntryHelper
      .getDraftFromServer(draft_id)
      .then(draft => {
        if (draft === null) {
          navigate('/notary/signing');
          logger.log('setDraftToEdit Signing', draft_id);
          toast.error("Draft doesn't exist.", {
            theme: 'colored',
            position: 'bottom-center',
            icon: faExclamation
          });
        } else {
          return new Promise((resolve, reject) => {
            if (draft && !_.isEmpty(draft)) {
              let now = moment().format();
              let draftMetadata = draft?.draft_metadata || {};
              let draftEntrydata = {
                type: c.actions.newEntry.start,
                draft_id: draft_id,
                signing_timestamp: null,
                localTimezone: null,
                userProfileGUID: null,
                startedAtDate: now,
                completedAtDate: null,
                promptForRatingOnComplete: false,
                hasSigningBeenFinished: false,
                practiceTour: false,
                Type: 'InPerson',
                location: {
                  address_1: '',
                  address_2: '',
                  city: '',
                  state: '',
                  zip: ''
                },
                notarization_details: {
                  notary_fee: '',
                  currency: 'USD',
                  notes: '',
                  pictures: [],
                  Signing_Fees: []
                },
                signers: [],
                notarization_documents: [],
                identifications: [],
                signatures: [],
                fingerprints: [],
                witnesses: [],
                recipientEmails: [],
                auditLog: [],
                ...draft?.entry_data
              };
              if (!draftEntrydata.requestGUID) {
                draftEntrydata.requestGUID =
                  signing?.draft_id === draft_id && signing.requestGUID
                    ? signing.requestGUID
                    : uuidv4();
              }
              logger.log('chk draftEntrydata', draftEntrydata);
              let currentStepVal = c.entrySteps.STEP_1;
              if (
                !!draft.draft_metadata?.completed_at ||
                !!draftEntrydata?.completedAtDate
              ) {
                setIsCompleted(true);
                currentStepVal = c.entrySteps.STEP_6;
              } else {
                setIsCompleted(false);
                currentStepVal = draftMetadata?.entry_step;
              }
              let currentStep =
                draftEntryHelper.getDraftStepName(currentStepVal);

              dispatch(actionSetDraftSaveError(null));
              dispatch(actionSetDraftSaveStatus(c.draftStatus.None));
              setStep(currentStep.id);

              if (draftEntrydata?.draft_id) {
                dispatch(actionReInstateSigning(draftEntrydata));
                navigate(
                  '/notary/signing/' +
                    draftEntryHelper.getDarftKeyFromId(draft_id)
                );
                // Mark ready only after step is restored so autosave cannot
                // persist the default mount step (1) over the draft progress.
                setIsDraftReady(true);
                resolve(true);
              } else {
                //logger.log('No draft found in signig, saving skipped');
              }
            } else {
              logger.warn(
                'draft was null or empty',
                userMaster?.GUID,
                draft_id,
                draft
              );
              resolve(null);
            }
          });
        }
      })
      .then(res => {})
      .catch(err => {
        logger.warn('setDraftToEdit failed', err);
        toast.error('Unable to load draft. Please try again.', {
          theme: 'colored',
          position: 'bottom-center',
          icon: faExclamation
        });
        navigate('/notary/drafts');
      });
  };

  const setBacktoDraftList = () => {
    segmentHelper.track(segmentHelper.events.SIGNING_BACK_TO_DRAFT_CLICK);

    if (step === 5 || step === 6) {
      handleNavigateWithoutSave();
    } else {
      // Show modal warning about unsaved data
      setShowUnsavedDataModal(true);
    }
  };

  const handleSaveAndNavigate = () => {
    // Save the draft before navigating
    saveSigningToDraft();
    setShowUnsavedDataModal(false);
    // Wait a reasonable time for the save to complete, then navigate
    // The saveSigningToDraft function handles setSavingDraft internally
    setTimeout(() => {
      navigate('/notary/drafts');
    }, 1500);
  };

  const handleNavigateWithoutSave = () => {
    setShowUnsavedDataModal(false);
    // Navigate without saving (current behavior)
    navigate('/notary/drafts');
  };

  const initialiseNewEntry = () => {
    setInitializingNewEntry(true);
    Promise.all([
      dispatch(refreshSavedLocation()),
      dispatch(refreshDocumentList()),
      dispatch(refreshDocumentCollections())
    ]).then(res => {
      setInitializingNewEntry(false);
      setReadyToStartEntry(true);
    });
  };

  useEffect(() => {
    if (readyToStartEntry)
      segmentHelper.track(segmentHelper.events.CREATE_NEW_ENTRY_CLICKED);
  }, [readyToStartEntry]);

  const createNewDraftEntry = () => {
    setReadyToStartEntry(false);
    if (!initializingDraftEntry) {
      setInitializingDraftEntry(true);
      segmentHelper.track(segmentHelper.events.NEW_ENTRY_CLICK_TO_PROCEED);
      let draft_id = null;
      dispatch(actionReset());
      setStep(1);
      setIsCompleted(false);
      dispatch(actionSetDraftSaveError(null));
      createNewDraftIdOrWarn().then(newDraftId => {
        draft_id = newDraftId;
        logger.log('createNewDraftIdOrWarn Signing', draft_id);
        let now = moment().format();

        // Seed the new server draft with the initial signing_timestamp. Navigating
        // into the draft immediately re-hydrates from the server (setDraftToEdit ->
        // getDraftFromServer); without this seed the empty server body would wipe the
        // pre-populated Notarization Date & Time. (Previously this was seeded to
        // localStorage; the server is now the source of truth.)
        let draftMetadata = {
          created: now,
          completed_at: null,
          last_updated: now,
          entry_step: c.entrySteps.STEP_1,
          source: 'Desktop'
        };
        let updatedSavedDraftPromise = draft_id
          ? dispatch(
              updateDraftOnServer(
                draftEntryHelper.getDarftKeyFromId(draft_id),
                utils.encodeObjectForBlobData({
                  draft_id: draft_id,
                  draft_data: draftMetadata,
                  entry_data: { signing_timestamp: now }
                })
              )
            ).catch(() => true)
          : Promise.resolve(true);

        Promise.all([updatedSavedDraftPromise]).then(() => {
          let signingResetPromise = new Promise((resolve, reject) => {
            dispatch(actionReset());
            resolve(true);
          });

          Promise.all([signingResetPromise]).then(() => {
            let signingActionStartPromise = new Promise((resolve, reject) => {
              dispatch(
                actionStart({
                  draft_id: draft_id,
                  promptForRatingOnComplete: false,
                  startedAtDate: now,
                  signing_timestamp: now
                })
              );
              resolve(true);
            });

            Promise.all([signingActionStartPromise]).then(() => {
              dispatch(actionLogSigningAction(`Signing Created.`));
              setInitializingDraftEntry(false);
              navigate(
                '/notary/signing/' +
                  draftEntryHelper.getDarftKeyFromId(draft_id)
              );
            });

            dispatch(
              actionAddNotification({
                message: 'Started New Journal Entry',
                to:
                  '/notary/signing/' +
                  draftEntryHelper.getDarftKeyFromId(draft_id)
              })
            );
          });
        });
      });
    }
  };

  return (
    <>
      <PageHeader
        //preTitle={urlDraftId ? 'New Signing' : 'Saved Signing Drafts'}
        title={isPractice !== '1' ? 'New Journal Entry' : 'Practice Module'}
        className="mb-3"
        titleTag="h2"
        image={corner6}
        col={{ lg: 8, md: 7, sm: 12 }}
        eCol={{ lg: 4, md: 5, sm: 12 }}
        eol={
          <>
            <Flex justifyContent="end" alignItems="center">
              <div className=" me-2">
                {savingDraft ? (
                  <span className="text-primary">
                    <Spinner
                      style={{ width: 16, height: 16 }}
                      variant="primary"
                      size="sm"
                    />{' '}
                    Saving Draft
                  </span>
                ) : urlDraftId ? (
                  <span className="text-success">
                    <FontAwesomeIcon icon={faCheckCircle} /> Draft saved
                  </span>
                ) : (
                  <></>
                )}
              </div>
              {isPractice !== '1' && urlDraftId ? (
                <Button
                  onClick={() => setBacktoDraftList()}
                  variant="falcon-primary"
                  className="me-2 mb-1"
                >
                  <FontAwesomeIcon icon={faArrowCircleLeft} /> Draft list
                </Button>
              ) : (
                <></>
              )}
            </Flex>
          </>
        }
      />
      {initializingDraftEntry ? (
        <Flex
          className={'vh-50'}
          alignItems={'center'}
          justifyContent={'center'}
          direction={'column'}
        >
          <Spinner variant="secondary" size="md" />{' '}
          <p className="mt-3">Starting.... entry</p>
        </Flex>
      ) : (
        <SigningProvider>
          {urlDraftId && !isDraftReady ? (
            <Flex
              className={'vh-50'}
              alignItems={'center'}
              justifyContent={'center'}
              direction={'column'}
            >
              <Spinner variant="secondary" size="md" />{' '}
              <p className="mt-3">Loading draft...</p>
            </Flex>
          ) : urlDraftId && isDraftReady ? (
            <SigningLayout
              variant={variant}
              validation={validation}
              progressBar={progressBar}
              saveSigningToDraft={saveSigningToDraft}
              setSavingDraft={setSavingDraft}
              setIsCompleted={setIsCompleted}
              isCompleted={isCompleted}
              step={step}
              setStep={setStep}
              stepTracker={stepTracker}
              setStepTracker={setStepTracker}
            />
          ) : (
            <></>
          )}
        </SigningProvider>
      )}

      {/* Unsaved Data Warning Modal */}
      <Modal
        show={showUnsavedDataModal}
        onHide={() => setShowUnsavedDataModal(false)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon
              icon={faExclamation}
              className="text-warning me-2"
            />
            Unsaved Changes
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>You have unsaved changes in your journal entry.</p>
          <p className="mb-0">
            Would you like to save your changes before leaving, or continue
            without saving?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-primary" onClick={handleNavigateWithoutSave}>
            Do not save
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveAndNavigate}
            disabled={savingDraft}
          >
            {savingDraft ? (
              <>
                <Spinner size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} className="me-2" />
                Save & Exit
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {!urlDraftId && (
        <Row className="mb-3 g-3">
          <Col lg={12}>
            <Card>
              <Card.Body>
                {initializingNewEntry && (
                  <Flex
                    className={'vh-50'}
                    alignItems={'center'}
                    justifyContent={'center'}
                    direction={'column'}
                  >
                    <Spinner variant="secondary" size="md" />{' '}
                    <p className="mt-3">Initializing... Draft</p>
                  </Flex>
                )}
                {readyToStartEntry && (
                  <Flex
                    alignItems={'center'}
                    justifyContent={'center'}
                    direction={'column'}
                  >
                    <p>
                      <FontAwesomeIcon icon={faCheckCircle} /> New entry pre
                      check completed & initialized.
                    </p>
                    <p>
                      <Button
                        variant="primary"
                        onClick={() => createNewDraftEntry()}
                      >
                        Click to proceed <FontAwesomeIcon icon={faForward} />
                      </Button>
                    </p>
                    {activeSubscriptions.length == 0 && (
                      <div className="pb-3">
                        <Alert variant="warning">
                          <p className="text-center">
                            You do not have an active subscription and will not
                            be able to upload new entries to the secure servers.
                            Any new entries created will be saved as a draft.
                            Please activate or upgrade your subscription to
                            resume normal journal functionality.
                          </p>
                        </Alert>
                      </div>
                    )}
                  </Flex>
                )}
              </Card.Body>
            </Card>
            <div className="mt-3">
              <FaqBasicCard
                faqs={faqs}
                header
                headerText="Frequently asked questions"
                bodyClass="bg-light"
                faqIdentity="Signing page"
              />
            </div>
          </Col>
        </Row>
      )}
    </>
  );
};

Signing.propTypes = {
  variant: PropTypes.oneOf(['pills']),
  validation: PropTypes.bool,
  progressBar: PropTypes.bool
};

export default Signing;
